import { TFunction } from 'i18next';

export type FieldValidationRule = {
  type: 'required' | 'min' | 'max';
  params?: {
    min?: number;
    max?: number;
  };
};

export function generatePlaceholderForInputField(
  validationRules: FieldValidationRule[],
  t: TFunction<string | string[], undefined>,
) {
  if (!validationRules || !validationRules.length) {
    return t('field_placeholders.input_optional');
  }

  const validationTests = validationRules.reduce<{
    isRequired: boolean;
    min: null | number;
    max: null | number;
  }>(
    (acc, next) => {
      if (next.type === 'required') {
        acc.isRequired = true;
      }

      if (next.type === 'min') {
        acc.min = next.params?.min
          ? next.params?.min == 1
            ? 0
            : next.params?.min
          : 0;
      }

      if (next.type === 'max') {
        acc.max = next.params?.max || 0;
      }

      return acc;
    },
    {
      isRequired: false,
      min: null,
      max: null,
    },
  );

  let result = validationTests.isRequired
    ? t('field_placeholders.input_required')
    : t('field_placeholders.input_optional');

  if (validationTests.min || validationTests.max) {
    if (validationTests.min && !validationTests.max) {
      result = `${result} (${t('field_placeholders.min_length', { number: validationTests.min })})`;
    } else if (validationTests.max && !validationTests.min) {
      result = `${result} (${t('field_placeholders.max_length', { number: validationTests.max })})`;
    } else {
      result = `${result} (${t('field_placeholders.min_max_length', {
        minNumber: validationTests.min,
        maxNumber: validationTests.max,
      })})`;
    }
  }
  return result;
}

export function generateSimpleRequiredOptionalPlaceholder(
  validationRules: FieldValidationRule[],
  t: TFunction<string | string[], undefined>,
) {
  if (!validationRules || !validationRules.length) {
    return t('field_placeholders.input_optional');
  }

  const isRequired = validationRules.find((e) => e.type === 'required');

  if (isRequired) {
    return t('field_placeholders.input_required');
  } else {
    return t('field_placeholders.input_optional');
  }
}

export function generatePlaceholderForDatePicker(
  validationRules: FieldValidationRule[],
  t: TFunction<string | string[], undefined>,
) {
  if (!validationRules || !validationRules.length) {
    return t('field_placeholders.select_date_optional');
  }

  const isRequired = validationRules.find((e) => e.type === 'required');

  if (isRequired) {
    return t('field_placeholders.select_date_required');
  } else {
    return t('field_placeholders.select_date_optional');
  }
}

export function generatePlaceholderForDropdownField(
  isMultiSelect: boolean,
  validationRules: FieldValidationRule[],
  t: TFunction<string | string[], undefined>,
) {
  const notRequiredPlaceholder = isMultiSelect
    ? t('field_placeholders.multi_select_optional')
    : t('field_placeholders.select_optional');

  if (!validationRules || !validationRules.length) {
    return notRequiredPlaceholder;
  }

  const isRequired = validationRules.find((e) => e.type === 'required');

  if (isRequired) {
    return isMultiSelect
      ? t('field_placeholders.multi_select_required')
      : t('field_placeholders.select_required');
  } else {
    return notRequiredPlaceholder;
  }
}
